import React, { useState, useEffect, useCallback } from 'react';
import {
  CreditCard,
  CheckCircle2,
  XCircle,
  AlertCircle,
  RefreshCw,
  Building,
  School,
  ShieldCheck,
  Clock,
  ChevronDown,
  ChevronUp,
  Calendar,
  Ticket,
  Users,
  Check,
  X,
  FileCheck2,
  FileCheck,
  Eye,
  BookOpen
} from 'lucide-react';
import { ExaminationPassModal } from '../../components/admissions/EnglishAdmissionsDocuments';
import './AdmissionsFeeApprovals.css';

const LEVEL_META = {
  preschool: { title: 'Preschool (KB / Playgroup)', tag: 'KB', color: '#10b981' },
  kindergarten: { title: 'Kindergarten (TK A & TK B)', tag: 'TK', color: '#06b6d4' },
  primary: { title: 'Primary School (SD Kelas 1 - 6)', tag: 'SD', color: '#3b82f6' },
  secondary: { title: 'Secondary School (SMP Kelas 7 - 9)', tag: 'SMP', color: '#8b5cf6' },
  sma: { title: 'High School (SMA - Coming Soon)', tag: 'SMA', color: '#f59e0b' },
};

function formatRupiah(num) {
  const n = Number(num || 0);
  return 'Rp ' + n.toLocaleString('id-ID');
}

function formatDateDisplay(dStr) {
  if (!dStr) return '-';
  try {
    const d = new Date(dStr);
    if (isNaN(d.getTime())) return dStr;
    return d.toLocaleDateString('id-ID', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch (_) {
    return dStr;
  }
}

export default function AdmissionsFeeApprovals({ apiBase, onFeeChange, initialCategory = 'levels' }) {
  const [activeCategory, setActiveCategory] = useState(initialCategory || 'levels');

  useEffect(() => {
    if (initialCategory) {
      setActiveCategory(initialCategory);
    }
  }, [initialCategory]); // 'levels' | 'periods' | 'test_slots' | 'promo_codes'
  const [levels, setLevels] = useState([]);
  const [configApprovals, setConfigApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [feedback, setFeedback] = useState(null);
  
  // Rejection Modals
  const [rejectModalLevel, setRejectModalLevel] = useState(null);
  const [rejectModalConfig, setRejectModalConfig] = useState(null);
  const [rejectNote, setRejectNote] = useState('');
  
  const [expandedLevels, setExpandedLevels] = useState({});
  const [passPreviewModal, setPassPreviewModal] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setFeedback(null);
    try {
      const [resLevels, resConfigs] = await Promise.all([
        fetch(`${apiBase}/admin/admissions/levels`, {
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        }),
        fetch(`${apiBase}/admin/admissions/config-approvals`, {
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        }),
      ]);

      const dataLevels = await resLevels.json();
      if (resLevels.ok && dataLevels?.ok && Array.isArray(dataLevels.data)) {
        setLevels(dataLevels.data);
      }

      if (resConfigs.ok) {
        const dataConfigs = await resConfigs.json();
        if (dataConfigs?.ok && Array.isArray(dataConfigs.data)) {
          setConfigApprovals(dataConfigs.data);
        }
      }
    } catch (_) {
      setFeedback({ type: 'error', text: 'Gagal terhubung ke server untuk memuat data pengajuan approval' });
    } finally {
      setLoading(false);
    }
  }, [apiBase]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const toggleExpand = (levelCode) => {
    setExpandedLevels((prev) => ({ ...prev, [levelCode]: !prev[levelCode] }));
  };

  // 1. Level Fee Decision
  const handleApproveLevelFee = async (levelCode) => {
    if (!window.confirm(`Konfirmasi: Setujui struktur biaya resmi baru untuk jenjang ${levelCode.toUpperCase()}?`)) {
      return;
    }
    setActionLoading(true);
    setFeedback(null);
    try {
      const res = await fetch(`${apiBase}/admin/admissions/levels/${levelCode}/approve-fees`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ decision: 'approve', note: 'Disetujui oleh Kepala Sekolah (Principal)' }),
      });
      const data = await res.json();
      if (res.ok && data?.ok) {
        setFeedback({ type: 'success', text: data.message || 'Biaya jenjang berhasil disetujui dan kini aktif!' });
        await loadData();
        if (onFeeChange) onFeeChange();
      } else {
        setFeedback({ type: 'error', text: data.message || 'Gagal menyetujui biaya jenjang' });
      }
    } catch (_) {
      setFeedback({ type: 'error', text: 'Terjadi kesalahan jaringan saat memproses approval' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleConfirmRejectLevel = async (e) => {
    e.preventDefault();
    if (!rejectModalLevel) return;
    setActionLoading(true);
    setFeedback(null);
    try {
      const res = await fetch(`${apiBase}/admin/admissions/levels/${rejectModalLevel.level_code}/approve-fees`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ decision: 'reject', note: rejectNote || 'Perubahan biaya ditolak oleh Kepala Sekolah.' }),
      });
      const data = await res.json();
      if (res.ok && data?.ok) {
        setFeedback({ type: 'info', text: 'Pengajuan perubahan biaya telah ditolak dan dikembalikan ke Admin Admissions.' });
        setRejectModalLevel(null);
        await loadData();
        if (onFeeChange) onFeeChange();
      } else {
        setFeedback({ type: 'error', text: data.message || 'Gagal menolak perubahan biaya' });
      }
    } catch (_) {
      setFeedback({ type: 'error', text: 'Terjadi kesalahan jaringan saat memproses penolakan' });
    } finally {
      setActionLoading(false);
    }
  };

  // 2. Config Approvals Decision (Periods, Slots, Promos)
  const handleConfigDecision = async (approvalId, decision, note = '') => {
    if (decision === 'approve' && !window.confirm('Konfirmasi: Setujui pengajuan ini dan terapkan langsung di sistem?')) {
      return;
    }
    setActionLoading(true);
    setFeedback(null);
    try {
      const res = await fetch(`${apiBase}/admin/admissions/config-approvals/${approvalId}/decision`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ decision, note }),
      });
      const data = await res.json();
      if (res.ok && data?.ok) {
        setFeedback({ type: 'success', text: data.message || 'Pengajuan berhasil diproses' });
        await loadData();
        if (onFeeChange) onFeeChange();
      } else {
        setFeedback({ type: 'error', text: data.message || 'Gagal memproses approval' });
      }
    } catch (_) {
      setFeedback({ type: 'error', text: 'Terjadi kesalahan jaringan saat memproses pengajuan' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleConfirmRejectConfig = async (e) => {
    e.preventDefault();
    if (!rejectModalConfig) return;
    await handleConfigDecision(rejectModalConfig.id, 'reject', rejectNote || 'Ditolak oleh Kepala Sekolah (Principal).');
    setRejectModalConfig(null);
  };

  // Counts
  const pendingLevelsCount = levels.filter(
    (l) => l.fee_approval_status === 'pending_principal' || Boolean(l.pending_fee_payload)
  ).length;

  const pendingPeriods = configApprovals.filter((c) => c.config_type === 'period');
  const pendingSlots = configApprovals.filter((c) => c.config_type === 'test_slot');
  const pendingPromos = configApprovals.filter((c) => c.config_type === 'promo_code');
  const pendingAssessments = configApprovals.filter((c) => c.config_type === 'assessment');

  const totalPendingAll = pendingLevelsCount + configApprovals.length;

  return (
    <div className="adm-fee-approvals-wrap">
      <div className="afa-header">
        <div className="afa-title-block">
          <div className="afa-icon-badge">
            <FileCheck2 size={24} color="#ffffff" />
          </div>
          <div>
            <h2>Pusat Persetujuan Konfigurasi PPDB & Biaya Masuk</h2>
            <p>
              Otoritas tunggal Kepala Sekolah (Principal) untuk mengesahkan Tarif Biaya Jenjang, Periode & Kuota Pendaftaran, Jadwal Tes Offline, dan Kode Diskon sebelum resmi aktif dan ditagihkan kepada orang tua siswa.
            </p>
          </div>
        </div>
        <button type="button" className="afa-refresh-btn" onClick={loadData} disabled={loading || actionLoading}>
          <RefreshCw size={15} className={loading ? 'spin' : ''} />
          <span>Segarkan Data</span>
        </button>
      </div>

      {feedback && (
        <div className={`afa-feedback-alert ${feedback.type}`}>
          {feedback.type === 'success' && <CheckCircle2 size={16} />}
          {feedback.type === 'error' && <XCircle size={16} />}
          {feedback.type === 'info' && <AlertCircle size={16} />}
          <span>{feedback.text}</span>
          <button type="button" className="afa-alert-close" onClick={() => setFeedback(null)}>
            <X size={14} />
          </button>
        </div>
      )}

      {/* 4 Approval Category Navigation Pills */}
      <div className="afa-category-tabs">
        <button
          type="button"
          className={`afa-cat-btn ${activeCategory === 'levels' ? 'active' : ''}`}
          onClick={() => setActiveCategory('levels')}
        >
          <CreditCard size={16} />
          <span>Biaya per Jenjang</span>
          {pendingLevelsCount > 0 && <span className="afa-pill-counter">{pendingLevelsCount}</span>}
        </button>

        <button
          type="button"
          className={`afa-cat-btn ${activeCategory === 'periods' ? 'active' : ''}`}
          onClick={() => setActiveCategory('periods')}
        >
          <Calendar size={16} />
          <span>Periode & Kuota</span>
          {pendingPeriods.length > 0 && <span className="afa-pill-counter">{pendingPeriods.length}</span>}
        </button>

        <button
          type="button"
          className={`afa-cat-btn ${activeCategory === 'test_slots' ? 'active' : ''}`}
          onClick={() => setActiveCategory('test_slots')}
        >
          <Clock size={16} />
          <span>Jadwal Sesi Tes</span>
          {pendingSlots.length > 0 && <span className="afa-pill-counter">{pendingSlots.length}</span>}
        </button>

        <button
          type="button"
          className={`afa-cat-btn ${activeCategory === 'assessment' ? 'active' : ''}`}
          onClick={() => setActiveCategory('assessment')}
        >
          <FileCheck size={16} />
          <span>Materi Asesmen &amp; Kartu Tes</span>
          {pendingAssessments.length > 0 && <span className="afa-pill-counter">{pendingAssessments.length}</span>}
        </button>

        <button
          type="button"
          className={`afa-cat-btn ${activeCategory === 'promo_codes' ? 'active' : ''}`}
          onClick={() => setActiveCategory('promo_codes')}
        >
          <Ticket size={16} />
          <span>Kode Promo & Diskon</span>
          {pendingPromos.length > 0 && <span className="afa-pill-counter">{pendingPromos.length}</span>}
        </button>
      </div>

      {/* CATEGORY 1: LEVEL FEES APPROVAL */}
      {activeCategory === 'levels' && (
        <div className="afa-levels-container">
          <div className="afa-section-info-banner">
            <div>
              <strong>Persetujuan Struktur Biaya Jenjang ({pendingLevelsCount} Menunggu)</strong>
              <p>Perubahan biaya yang diajukan oleh Admin Admissions tidak akan ditagihkan ke orang tua calon siswa sampai Anda menyetujuinya.</p>
            </div>
          </div>

          {loading ? (
            <div className="afa-loading-state">
              <RefreshCw size={24} className="spin" />
              <span>Memuat data konfigurasi tarif biaya...</span>
            </div>
          ) : levels.length === 0 ? (
            <div className="afa-empty-state">
              <AlertCircle size={28} />
              <p>Belum ada konfigurasi jenjang yang terdaftar.</p>
            </div>
          ) : (
            levels.map((lvl) => {
              const meta = LEVEL_META[lvl.level_code] || {
                title: lvl.level_name,
                tag: lvl.tag_code,
                color: '#3b82f6',
              };
              const isPending = lvl.fee_approval_status === 'pending_principal' || Boolean(lvl.pending_fee_payload);
              const pending = lvl.pending_fee_payload || {};
              const isExpanded = expandedLevels[lvl.level_code] !== false;

              const feeItems = [
                { key: 'registration_fee', label: 'Biaya Pendaftaran Formulir (Registration Fee)' },
                { key: 'test_reg_form_fee', label: 'Biaya Formulir Tes Seleksi Masuk' },
                { key: 'admission_fee', label: 'Uang Masuk / Matrikulasi (Admission Fee)' },
                { key: 'development_fee', label: 'Uang Pangkal Total (Development Fee)' },
                { key: 'df_1_amount', label: '— Termin DF 1 (Saat Daftar Ulang)' },
                { key: 'df_2_amount', label: '— Termin DF 2' },
                { key: 'df_3_amount', label: '— Termin DF 3' },
                { key: 'books_activity_fee', label: 'Paket Buku Pelajaran & Student Activity Fee' },
                { key: 'tuition_fee', label: 'SPP Bulanan (Monthly Tuition Fee)' },
              ];

              return (
                <div
                  key={lvl.level_code}
                  className={`afa-level-card ${isPending ? 'has-pending' : 'is-approved'}`}
                  style={{ borderLeftColor: isPending ? '#f59e0b' : meta.color }}
                >
                  <div className="afa-card-header" onClick={() => toggleExpand(lvl.level_code)}>
                    <div className="afa-card-title-group">
                      <span className="afa-level-tag" style={{ backgroundColor: meta.color }}>
                        {meta.tag}
                      </span>
                      <div>
                        <h3>{lvl.level_name}</h3>
                        <div className="afa-card-meta-line">
                          <span>Kategori: {lvl.fee_category === 'early_years' ? 'Early Years' : 'School Years'}</span>
                          <span className="dot">•</span>
                          <span>BCA: {lvl.bank_account_number || '548-098-7788'}</span>
                          <span className="dot">•</span>
                          <span>Tersedia: {Array.isArray(lvl.available_grades) ? lvl.available_grades.join(', ') : '-'}</span>
                        </div>
                      </div>
                    </div>

                    <div className="afa-card-status-group">
                      {isPending ? (
                        <span className="afa-badge-pending">
                          <Clock size={13} />
                          <span>Menunggu Approval</span>
                        </span>
                      ) : (
                        <span className="afa-badge-approved">
                          <CheckCircle2 size={13} />
                          <span>Biaya Disetujui</span>
                        </span>
                      )}
                      <button type="button" className="afa-toggle-btn" aria-label="Toggle">
                        {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                      </button>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="afa-card-body">
                      {isPending && (
                        <div className="afa-pending-alert">
                          <AlertCircle size={16} />
                          <div>
                            <strong>Admin Admissions mengajukan perubahan tarif biaya untuk jenjang ini:</strong>
                            <p>Periksa perbandingan antara tarif yang sedang aktif dan tarif baru yang diajukan di bawah ini:</p>
                          </div>
                        </div>
                      )}

                      <div className="afa-table-wrap">
                        <table className="afa-diff-table">
                          <thead>
                            <tr>
                              <th>Komponen Tarif Biaya</th>
                              <th className="text-right">Tarif Resmi Saat Ini</th>
                              {isPending && <th className="text-right afa-col-pending">Tarif Baru Diajukan</th>}
                              {isPending && <th className="text-right">Selisih</th>}
                            </tr>
                          </thead>
                          <tbody>
                            {feeItems.map(({ key, label }) => {
                              const currVal = Number(lvl[key] || 0);
                              const pendVal = isPending && pending[key] !== undefined ? Number(pending[key]) : currVal;
                              const diff = pendVal - currVal;
                              const isDifferent = isPending && diff !== 0;

                              return (
                                <tr key={key} className={isDifferent ? 'afa-row-changed' : ''}>
                                  <td>{label}</td>
                                  <td className="text-right">{formatRupiah(currVal)}</td>
                                  {isPending && (
                                    <td className="text-right afa-col-pending">
                                      <strong style={{ color: isDifferent ? '#2563eb' : 'inherit' }}>
                                        {formatRupiah(pendVal)}
                                      </strong>
                                    </td>
                                  )}
                                  {isPending && (
                                    <td className="text-right">
                                      {isDifferent ? (
                                        <span className={diff > 0 ? 'afa-diff-up' : 'afa-diff-down'}>
                                          {diff > 0 ? `+${formatRupiah(diff)}` : `-${formatRupiah(Math.abs(diff))}`}
                                        </span>
                                      ) : (
                                        <span className="afa-diff-equal">-</span>
                                      )}
                                    </td>
                                  )}
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>

                      {isPending ? (
                        <div className="afa-actions-bar">
                          <div className="afa-actions-info">
                            <span>Klik Setujui untuk mengaktifkan tarif ini di portal pendaftaran orang tua.</span>
                          </div>
                          <div className="afa-actions-btns">
                            <button
                              type="button"
                              className="afa-btn-reject"
                              disabled={actionLoading}
                              onClick={() => { setRejectModalLevel(lvl); setRejectNote(''); }}
                            >
                              <XCircle size={15} />
                              <span>Tolak Pengajuan</span>
                            </button>
                            <button
                              type="button"
                              className="afa-btn-approve"
                              disabled={actionLoading}
                              onClick={() => handleApproveLevelFee(lvl.level_code)}
                            >
                              <CheckCircle2 size={15} />
                              <span>Setujui Tarif Resmi (Approve)</span>
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="afa-approved-info-bar">
                          <CheckCircle2 size={16} color="#059669" />
                          <span>
                            Tarif jenjang ini berstatus <strong>RESMI & AKTIF</strong>. Tidak ada pengajuan perubahan tertunda.
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}

      {/* CATEGORY 2: PERIODS APPROVAL */}
      {activeCategory === 'periods' && (
        <div className="afa-config-container">
          <div className="afa-section-info-banner">
            <div>
              <strong>Persetujuan Gelombang Periode Pendaftaran & Kuota ({pendingPeriods.length} Menunggu)</strong>
              <p>Penambahan periode baru, perubahan kuota kursi tiap jenjang, atau penghapusan periode wajib disetujui Kepala Sekolah.</p>
            </div>
          </div>

          {pendingPeriods.length === 0 ? (
            <div className="afa-empty-card">
              <CheckCircle2 size={32} color="#10b981" />
              <h3>Semua Periode Pendaftaran Telah Disetujui</h3>
              <p>Tidak ada pengajuan pembuatan atau perubahan periode pendaftaran yang sedang menunggu persetujuan.</p>
            </div>
          ) : (
            pendingPeriods.map((item) => {
              const p = item.payload || {};
              const orig = item.original || {};
              const isCreate = item.action_type === 'create';
              const isUpdate = item.action_type === 'update';
              const isDelete = item.action_type === 'delete';

              return (
                <div key={item.id} className="afa-config-card">
                  <div className="afa-config-card-header">
                    <div>
                      <span className={`afa-action-badge action-${item.action_type}`}>
                        {isCreate ? 'TAMBAH PERIODE BARU' : isUpdate ? 'PERBARUI PERIODE & KUOTA' : 'HAPUS PERIODE'}
                      </span>
                      <h3 style={{ margin: '6px 0 2px' }}>{item.target_label}</h3>
                      <div className="afa-request-meta">
                        Diajukan oleh: <strong>{item.requested_by_name}</strong> • Waktu: {formatDateDisplay(item.created_at)}
                      </div>
                    </div>
                  </div>

                  <div className="afa-config-card-body">
                    <div className="afa-detail-grid">
                      <div>
                        <span className="afa-detail-label">Tahun Ajaran:</span>
                        <strong>{p.academic_year || orig.academic_year || '2026/2027'}</strong>
                      </div>
                      <div>
                        <span className="afa-detail-label">Rentang Tanggal:</span>
                        <strong>{p.start_date || orig.start_date} s/d {p.end_date || orig.end_date}</strong>
                      </div>
                      <div>
                        <span className="afa-detail-label">Status Aktif:</span>
                        <strong>{(p.is_active ?? orig.is_active) ? 'Aktif Menerima Pendaftaran' : 'Ditutup Sementara'}</strong>
                      </div>
                    </div>

                    <h4 style={{ margin: '14px 0 6px', fontSize: '0.85rem' }}>Rincian Kuota Kursi yang Diajukan:</h4>
                    <div className="afa-quota-grid">
                      {['preschool', 'kindergarten', 'primary', 'secondary'].map((lc) => (
                        <div key={lc} className="afa-quota-item">
                          <span style={{ textTransform: 'uppercase' }}>{lc}</span>
                          <strong>{(p.quotas && p.quotas[lc]) ?? (orig.quotas_json && JSON.parse(orig.quotas_json || '{}')[lc]) ?? '-'} Kursi</strong>
                        </div>
                      ))}
                    </div>

                    {p.notes && (
                      <p style={{ margin: '10px 0 0', fontSize: '0.82rem', color: '#64748b' }}>
                        Catatan Admin: {p.notes}
                      </p>
                    )}
                  </div>

                  <div className="afa-actions-bar">
                    <div className="afa-actions-info">
                      <span>Persetujuan ini akan langsung memperbarui periode pendaftaran di sistem.</span>
                    </div>
                    <div className="afa-actions-btns">
                      <button
                        type="button"
                        className="afa-btn-reject"
                        disabled={actionLoading}
                        onClick={() => { setRejectModalConfig(item); setRejectNote(''); }}
                      >
                        <XCircle size={15} />
                        <span>Tolak Pengajuan</span>
                      </button>
                      <button
                        type="button"
                        className="afa-btn-approve"
                        disabled={actionLoading}
                        onClick={() => handleConfigDecision(item.id, 'approve')}
                      >
                        <CheckCircle2 size={15} />
                        <span>Setujui Periode (Approve)</span>
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* CATEGORY 3: TEST SLOTS APPROVAL */}
      {activeCategory === 'test_slots' && (
        <div className="afa-config-container">
          <div className="afa-section-info-banner">
            <div>
              <strong>Persetujuan Jadwal Sesi Tes Offline ({pendingSlots.length} Menunggu)</strong>
              <p>Jadwal observasi & tes seleksi offline bertingkat yang diajukan oleh Admin Admissions.</p>
            </div>
          </div>

          {pendingSlots.length === 0 ? (
            <div className="afa-empty-card">
              <CheckCircle2 size={32} color="#10b981" />
              <h3>Semua Jadwal Tes Telah Disetujui</h3>
              <p>Tidak ada pengajuan jadwal sesi tes offline yang sedang menunggu persetujuan.</p>
            </div>
          ) : (
            pendingSlots.map((item) => {
              const p = item.payload || {};
              const orig = item.original || {};
              const isCreate = item.action_type === 'create';
              const isUpdate = item.action_type === 'update';

              return (
                <div key={item.id} className="afa-config-card">
                  <div className="afa-config-card-header">
                    <div>
                      <span className={`afa-action-badge action-${item.action_type}`}>
                        {isCreate ? 'JADWAL TES BARU' : isUpdate ? 'PERUBAHAN JADWAL TES' : 'HAPUS JADWAL TES'}
                      </span>
                      <h3 style={{ margin: '6px 0 2px' }}>{item.target_label}</h3>
                      <div className="afa-request-meta">
                        Diajukan oleh: <strong>{item.requested_by_name}</strong> • Waktu: {formatDateDisplay(item.created_at)}
                      </div>
                    </div>
                  </div>

                  <div className="afa-config-card-body">
                    <div className="afa-detail-grid">
                      <div>
                        <span className="afa-detail-label">Tanggal Pelaksanaan:</span>
                        <strong>{p.test_date || orig.test_date}</strong>
                      </div>
                      <div>
                        <span className="afa-detail-label">Waktu:</span>
                        <strong>{p.test_time || orig.test_time || '09:00 - 11:00 WIB'}</strong>
                      </div>
                      <div>
                        <span className="afa-detail-label">Lokasi Kampus:</span>
                        <strong>{p.campus_location || orig.campus_location}</strong>
                      </div>
                      <div>
                        <span className="afa-detail-label">Ruangan:</span>
                        <strong>{p.room_name || orig.room_name || 'Ruang Assessment'}</strong>
                      </div>
                      <div>
                        <span className="afa-detail-label">Kapasitas Kursi:</span>
                        <strong>{p.slot_quota || orig.slot_quota} Peserta</strong>
                      </div>
                      <div>
                        <span className="afa-detail-label">Jenjang Sasaran:</span>
                        <strong>{String(p.level_code || orig.level_code || 'all').toUpperCase()}</strong>
                      </div>
                    </div>
                  </div>

                  <div className="afa-actions-bar">
                    <div className="afa-actions-info">
                      <span>Jadwal tes ini akan langsung tersedia untuk dijadwalkan ke calon siswa.</span>
                    </div>
                    <div className="afa-actions-btns">
                      <button
                        type="button"
                        className="afa-btn-reject"
                        disabled={actionLoading}
                        onClick={() => { setRejectModalConfig(item); setRejectNote(''); }}
                      >
                        <XCircle size={15} />
                        <span>Tolak Jadwal</span>
                      </button>
                      <button
                        type="button"
                        className="afa-btn-approve"
                        disabled={actionLoading}
                        onClick={() => handleConfigDecision(item.id, 'approve')}
                      >
                        <CheckCircle2 size={15} />
                        <span>Setujui Jadwal Tes (Approve)</span>
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* CATEGORY 4: PROMO CODES APPROVAL */}
      {activeCategory === 'promo_codes' && (
        <div className="afa-config-container">
          <div className="afa-section-info-banner">
            <div>
              <strong>Persetujuan Kode Voucher & Diskon PPDB ({pendingPromos.length} Menunggu)</strong>
              <p>Semua kode promo diskon uang pangkal / open house harus disetujui Kepala Sekolah sebelum dapat digunakan orang tua.</p>
            </div>
          </div>

          {pendingPromos.length === 0 ? (
            <div className="afa-empty-card">
              <CheckCircle2 size={32} color="#10b981" />
              <h3>Semua Kode Promo Telah Disetujui</h3>
              <p>Tidak ada pengajuan pembuatan atau penghapusan kode promo yang menunggu persetujuan.</p>
            </div>
          ) : (
            pendingPromos.map((item) => {
              const p = item.payload || {};
              const orig = item.original || {};
              const isCreate = item.action_type === 'create';

              return (
                <div key={item.id} className="afa-config-card">
                  <div className="afa-config-card-header">
                    <div>
                      <span className={`afa-action-badge action-${item.action_type}`}>
                        {isCreate ? 'VOUCHER PROMO BARU' : 'PENGHAPUSAN KODE PROMO'}
                      </span>
                      <h3 style={{ margin: '6px 0 2px' }}>{item.target_label}</h3>
                      <div className="afa-request-meta">
                        Diajukan oleh: <strong>{item.requested_by_name}</strong> • Waktu: {formatDateDisplay(item.created_at)}
                      </div>
                    </div>
                  </div>

                  <div className="afa-config-card-body">
                    <div className="afa-detail-grid">
                      <div>
                        <span className="afa-detail-label">Kode Voucher:</span>
                        <strong style={{ fontFamily: 'monospace', fontSize: '1.05rem', color: '#2563eb' }}>
                          {p.promo_code || orig.promo_code}
                        </strong>
                      </div>
                      <div>
                        <span className="afa-detail-label">Nama Promo:</span>
                        <strong>{p.promo_title || orig.promo_title}</strong>
                      </div>
                      <div>
                        <span className="afa-detail-label">Besaran Diskon:</span>
                        <strong style={{ color: '#059669' }}>
                          {(p.discount_type || orig.discount_type) === 'percentage'
                            ? `${p.discount_value || orig.discount_value}% dari Development Fee`
                            : formatRupiah(p.discount_value || orig.discount_value)}
                        </strong>
                      </div>
                      <div>
                        <span className="afa-detail-label">Maksimal Kuota:</span>
                        <strong>{p.max_quota || orig.max_quota || 100} Penggunaan</strong>
                      </div>
                      <div>
                        <span className="afa-detail-label">Masa Berlaku:</span>
                        <strong>{p.valid_until || orig.valid_until || 'Tidak Terbatas'}</strong>
                      </div>
                    </div>
                  </div>

                  <div className="afa-actions-bar">
                    <div className="afa-actions-info">
                      <span>Kode promo ini akan langsung dapat diverifikasi oleh orang tua di portal pendaftaran.</span>
                    </div>
                    <div className="afa-actions-btns">
                      <button
                        type="button"
                        className="afa-btn-reject"
                        disabled={actionLoading}
                        onClick={() => { setRejectModalConfig(item); setRejectNote(''); }}
                      >
                        <XCircle size={15} />
                        <span>Tolak Voucher</span>
                      </button>
                      <button
                        type="button"
                        className="afa-btn-approve"
                        disabled={actionLoading}
                        onClick={() => handleConfigDecision(item.id, 'approve')}
                      >
                        <CheckCircle2 size={15} />
                        <span>Setujui Voucher (Approve)</span>
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* CATEGORY 5: ASSESSMENT CURRICULUM & RULES APPROVAL */}
      {activeCategory === 'assessment' && (
        <div className="afa-config-container">
          <div className="afa-section-info-banner">
            <div>
              <strong>Persetujuan Kurikulum Materi Asesmen &amp; Kartu Tes ({pendingAssessments.length} Menunggu)</strong>
              <p>Wewenang penuh Kepala Sekolah (Principal) untuk mengesahkan topik asesmen, format seleksi, perlengkapan wajib peserta, tata tertib, dan instruksi orang tua yang dicetak pada Kartu Tanda Peserta.</p>
            </div>
          </div>

          {pendingAssessments.length === 0 ? (
            <div className="afa-empty-card" style={{ marginBottom: 24 }}>
              <CheckCircle2 size={32} color="#10b981" />
              <h3>Semua Materi Asesmen &amp; Ketentuan Kartu Tes Telah Disetujui</h3>
              <p>Tidak ada pengajuan materi asesmen atau kartu tes yang sedang menunggu persetujuan Kepala Sekolah.</p>
            </div>
          ) : (
            pendingAssessments.map((item) => {
              const p = item.payload || {};
              const orig = item.original || {};

              return (
                <div key={item.id} className="afa-config-card" style={{ borderLeftColor: '#0284c7' }}>
                  <div className="afa-config-card-header">
                    <div>
                      <span className="afa-action-badge action-update">
                        USULAN PERUBAHAN MATERI ASESMEN
                      </span>
                      <h3 style={{ margin: '6px 0 2px' }}>{item.target_label}</h3>
                      <div className="afa-request-meta">
                        Diajukan oleh: <strong>{item.requested_by_name}</strong> • Waktu: {formatDateDisplay(item.created_at)}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="afa-btn-preview-pass"
                      onClick={() => {
                        setPassPreviewModal({
                          id: 9999,
                          registration_no: 'REG-PHS-PROPOSED',
                          child_full_name: 'Jonathan Wijaya (Contoh Siswa)',
                          level_name: item.target_label.replace(/^Materi\s+Asesmen:\s*/i, ''),
                          period_name: 'Gelombang 1 (Early Bird)',
                          academic_year: '2026/2027',
                          birth_place: 'Jakarta',
                          birth_date: '2014-05-18',
                          gender: 'male',
                          previous_school: 'SD Bintang Kejora Jakarta',
                          father_name: 'Hendrawan Wijaya',
                          parent_phone: '0812-3456-7890',
                          test_schedule: {
                            slot_name: 'Sesi Tes & Observasi Masuk Batch 1',
                            test_date: '2026-12-12',
                            start_time: '09:00',
                            end_time: '11:00',
                            campus_location: 'Campus 1 (Pos Pengumben) - Jl. Pos Pengumben Lama No.13, Jakarta Barat',
                            room_name: 'Ruang Asesmen Terpadu 102',
                            assessment_title: p.assessment_title || orig.assessment_title,
                            assessment_subjects: p.assessment_subjects || orig.assessment_subjects,
                            assessment_preparation: p.assessment_preparation || orig.assessment_preparation,
                            assessment_rules: p.assessment_rules || orig.assessment_rules,
                            parent_instructions: p.parent_instructions || orig.parent_instructions,
                          }
                        });
                      }}
                    >
                      <Eye size={15} />
                      <span>Pratinjau Kartu Peserta Tes (PDF)</span>
                    </button>
                  </div>

                  <div className="afa-config-card-body">
                    <div className="afa-detail-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
                      <div>
                        <span className="afa-detail-label">Judul / Format Asesmen:</span>
                        <strong style={{ color: '#0369a1' }}>{p.assessment_title || orig.assessment_title}</strong>
                      </div>
                      <div>
                        <span className="afa-detail-label">Fokus Materi / Aspek yang Diteskan:</span>
                        <strong style={{ color: '#1e293b' }}>{p.assessment_subjects || orig.assessment_subjects}</strong>
                      </div>
                    </div>

                    {(p.assessment_description || orig.assessment_description) && (
                      <div style={{ marginTop: 12 }}>
                        <span className="afa-detail-label">Deskripsi Alur Pelaksanaan Ujian:</span>
                        <p style={{ margin: '4px 0 0', fontSize: '0.85rem', color: '#475569', lineHeight: 1.5 }}>
                          {p.assessment_description || orig.assessment_description}
                        </p>
                      </div>
                    )}

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16, marginTop: 14 }}>
                      <div style={{ background: '#f8fafc', padding: 12, borderRadius: 8, border: '1px solid #e2e8f0' }}>
                        <span className="afa-detail-label" style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6, fontWeight: 700, color: '#0f172a' }}>
                          <BookOpen size={14} color="#0284c7" />
                          <span>Perlengkapan yang Wajib Dibawa:</span>
                        </span>
                        <pre style={{ margin: 0, fontFamily: 'inherit', fontSize: '0.82rem', whiteSpace: 'pre-wrap', lineHeight: 1.5, color: '#334155' }}>
                          {p.assessment_preparation || orig.assessment_preparation || '-'}
                        </pre>
                      </div>

                      <div style={{ background: '#f8fafc', padding: 12, borderRadius: 8, border: '1px solid #e2e8f0' }}>
                        <span className="afa-detail-label" style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6, fontWeight: 700, color: '#059669' }}>
                          <FileCheck size={14} color="#059669" />
                          <span>Tata Tertib &amp; Ketentuan Peserta:</span>
                        </span>
                        <pre style={{ margin: 0, fontFamily: 'inherit', fontSize: '0.82rem', whiteSpace: 'pre-wrap', lineHeight: 1.5, color: '#334155' }}>
                          {p.assessment_rules || orig.assessment_rules || '-'}
                        </pre>
                      </div>
                    </div>

                    {(p.parent_instructions || orig.parent_instructions) && (
                      <div style={{ marginTop: 12, background: '#f0fdf4', padding: '10px 14px', borderRadius: 8, border: '1px solid #bbf7d0' }}>
                        <span className="afa-detail-label" style={{ color: '#166534', fontWeight: 700 }}>Catatan Khusus Orang Tua / Pendamping &amp; Ruang Tunggu:</span>
                        <p style={{ margin: '4px 0 0', fontSize: '0.84rem', color: '#14532d', lineHeight: 1.5 }}>
                          {p.parent_instructions || orig.parent_instructions}
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="afa-actions-bar">
                    <div className="afa-actions-info">
                      <span>Persetujuan ini akan langsung memperbarui kartu tanda peserta tes calon siswa di jenjang ini.</span>
                    </div>
                    <div className="afa-actions-btns">
                      <button
                        type="button"
                        className="afa-btn-reject"
                        disabled={actionLoading}
                        onClick={() => { setRejectModalConfig(item); setRejectNote(''); }}
                      >
                        <XCircle size={15} />
                        <span>Tolak Usulan</span>
                      </button>
                      <button
                        type="button"
                        className="afa-btn-approve"
                        disabled={actionLoading}
                        onClick={() => handleConfigDecision(item.id, 'approve')}
                      >
                        <CheckCircle2 size={15} />
                        <span>Setujui Materi Asesmen (Approve)</span>
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}

          {/* Active Level Overview List */}
          <div className="afa-active-levels-overview">
            <h3 style={{ fontSize: '1rem', fontWeight: 700, margin: '20px 0 10px', color: '#0f172a' }}>
              Kurikulum Materi Asesmen &amp; Ketentuan Kartu Tes Resmi per Jenjang
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 14 }}>
              {levels.map((lvl) => {
                return (
                  <div key={lvl.level_code} style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 10, padding: 14, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                        <strong style={{ fontSize: '0.92rem', color: '#0f172a' }}>{lvl.level_name}</strong>
                        <span style={{ fontSize: '0.74rem', background: '#ecfdf5', color: '#065f46', padding: '2px 8px', borderRadius: 9999, fontWeight: 700 }}>
                          Resmi Aktif
                        </span>
                      </div>
                      <div style={{ fontSize: '0.84rem', color: '#0369a1', fontWeight: 600, marginBottom: 4 }}>
                        {lvl.assessment_title || 'Asesmen Standar'}
                      </div>
                      <p style={{ fontSize: '0.8rem', color: '#64748b', margin: '0 0 12px', lineHeight: 1.45 }}>
                        {lvl.assessment_subjects || 'Literasi, Numerasi, Karakter & Wawancara'}
                      </p>
                    </div>
                    <button
                      type="button"
                      className="afa-btn-preview-pass-sm"
                      onClick={() => {
                        setPassPreviewModal({
                          id: 9999,
                          registration_no: 'REG-PHS-OFFICIAL',
                          child_full_name: 'Jonathan Wijaya (Contoh Siswa)',
                          level_name: lvl.level_name,
                          period_name: 'Gelombang 1 (Early Bird)',
                          academic_year: '2026/2027',
                          birth_place: 'Jakarta',
                          birth_date: '2014-05-18',
                          gender: 'male',
                          previous_school: 'SD Bintang Kejora Jakarta',
                          father_name: 'Hendrawan Wijaya',
                          parent_phone: '0812-3456-7890',
                          test_schedule: {
                            slot_name: 'Sesi Tes & Observasi Masuk Batch 1',
                            test_date: '2026-12-12',
                            start_time: '09:00',
                            end_time: '11:00',
                            campus_location: 'Campus 1 (Pos Pengumben) - Jl. Pos Pengumben Lama No.13, Jakarta Barat',
                            room_name: 'Ruang Asesmen Terpadu 102',
                            assessment_title: lvl.assessment_title,
                            assessment_subjects: lvl.assessment_subjects,
                            assessment_preparation: lvl.assessment_preparation,
                            assessment_rules: lvl.assessment_rules,
                            parent_instructions: lvl.parent_instructions,
                          }
                        });
                      }}
                    >
                      <Eye size={13} />
                      <span>Pratinjau Kartu Peserta Tes (PDF)</span>
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal for Level Fee */}
      {rejectModalLevel && (
        <div className="afa-modal-overlay" onClick={() => setRejectModalLevel(null)}>
          <div className="afa-modal-box" onClick={(e) => e.stopPropagation()}>
            <div className="afa-modal-head">
              <h3>Tolak Pengajuan Biaya: {rejectModalLevel.level_name}</h3>
              <button type="button" className="afa-modal-close" onClick={() => setRejectModalLevel(null)}>
                <XCircle size={18} />
              </button>
            </div>
            <form onSubmit={handleConfirmRejectLevel} className="afa-modal-body">
              <p>
                Berikan catatan revisi atau alasan penolakan agar Admin Admissions dapat menyesuaikan nominal biaya sesuai instruksi Kepala Sekolah:
              </p>
              <textarea
                value={rejectNote}
                onChange={(e) => setRejectNote(e.target.value)}
                rows={4}
                required
                placeholder="Contoh: Nominal SPP terlalu tinggi dibandingkan tahun ajaran lalu, mohon diskusikan kembali..."
                className="afa-modal-textarea"
              />
              <div className="afa-modal-actions">
                <button
                  type="button"
                  className="afa-btn-secondary"
                  onClick={() => setRejectModalLevel(null)}
                  disabled={actionLoading}
                >
                  Batal
                </button>
                <button type="submit" className="afa-btn-danger" disabled={actionLoading}>
                  {actionLoading ? 'Menyimpan...' : 'Konfirmasi Penolakan'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reject Modal for Config (Period, Slot, Promo) */}
      {rejectModalConfig && (
        <div className="afa-modal-overlay" onClick={() => setRejectModalConfig(null)}>
          <div className="afa-modal-box" onClick={(e) => e.stopPropagation()}>
            <div className="afa-modal-head">
              <h3>Tolak Pengajuan: {rejectModalConfig.target_label}</h3>
              <button type="button" className="afa-modal-close" onClick={() => setRejectModalConfig(null)}>
                <XCircle size={18} />
              </button>
            </div>
            <form onSubmit={handleConfirmRejectConfig} className="afa-modal-body">
              <p>
                Berikan instruksi revisi atau alasan penolakan untuk Admin Admissions:
              </p>
              <textarea
                value={rejectNote}
                onChange={(e) => setRejectNote(e.target.value)}
                rows={4}
                required
                placeholder="Contoh: Kuota Primary terlalu sedikit, mohon ditambah menjadi 60 siswa..."
                className="afa-modal-textarea"
              />
              <div className="afa-modal-actions">
                <button
                  type="button"
                  className="afa-btn-secondary"
                  onClick={() => setRejectModalConfig(null)}
                  disabled={actionLoading}
                >
                  Batal
                </button>
                <button type="submit" className="afa-btn-danger" disabled={actionLoading}>
                  {actionLoading ? 'Menyimpan...' : 'Konfirmasi Penolakan'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Live Preview Modal for Test Pass */}
      {passPreviewModal && (
        <ExaminationPassModal
          app={passPreviewModal}
          onClose={() => setPassPreviewModal(null)}
        />
      )}
    </div>
  );
}
